import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from 'prop-types';

// Cloudflare Turnstile bot check (GPH-523). Loaded with the explicit render API
// so the form controls when it mounts, and with no extra npm package.
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Resolves once the script has defined window.turnstile.
function loadTurnstile(){
    return new Promise((resolve, reject) => {
        if(window.turnstile){
            resolve(window.turnstile);
            return;
        }

        const existing = document.getElementById(SCRIPT_ID);
        const script = existing ?? document.createElement('script');

        script.addEventListener('load', () => {
            if(window.turnstile){
                resolve(window.turnstile);
            }
            else{
                reject(new Error('turnstile script loaded but window.turnstile is undefined'));
            }
        }, { once: true });
        script.addEventListener('error', () => reject(new Error('turnstile script failed to load')), { once: true });

        if(!existing){
            script.id = SCRIPT_ID;
            script.src = SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    });
}

// "interaction-only" keeps the widget invisible unless Cloudflare decides the
// visitor needs a challenge, so most people never see it.
const Turnstile = forwardRef(function Turnstile({ action, onVerify, onUnavailable = () => {} }, ref){
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    // Refs so the form re-rendering on every keystroke never re-mints the widget.
    const onVerifyRef = useRef(onVerify);
    const onUnavailableRef = useRef(onUnavailable);

    useEffect(() => {
        onVerifyRef.current = onVerify;
        onUnavailableRef.current = onUnavailable;
    }, [onVerify, onUnavailable]);

    const siteKey = process.env.TURNSTILE_SITE_KEY;

    // Tokens are single-use, so the form calls this after every submit.
    const reset = useCallback(() => {
        if(widgetIdRef.current && window.turnstile){
            window.turnstile.reset(widgetIdRef.current);
        }
        onVerifyRef.current('');
    }, []);

    useImperativeHandle(ref, () => ({ reset }), [reset]);

    useEffect(() => {
        if(!siteKey){
            onUnavailableRef.current('not_configured');
            return;
        }
        if(!containerRef.current){
            return;
        }

        let cancelled = false;
        const container = containerRef.current;

        loadTurnstile()
            .then((turnstile) => {
                if(cancelled || widgetIdRef.current){
                    return;
                }
                widgetIdRef.current = turnstile.render(container, {
                    sitekey: siteKey,
                    action: action,
                    appearance: 'interaction-only',
                    callback: (token) => onVerifyRef.current(token),
                    // A stale token would only fail on the server, so clear it.
                    'expired-callback': () => onVerifyRef.current(''),
                    // Let the form submit anyway so the visitor gets the server's message
                    // instead of waiting forever. A later retry can still deliver a token.
                    'error-callback': () => {
                        onVerifyRef.current('');
                        onUnavailableRef.current('challenge_error');
                    },
                });
            })
            .catch((error) => {
                console.error('[turnstile] widget failed to load', error);
                if(!cancelled){
                    onUnavailableRef.current('load_failed');
                }
            });

        return () => {
            cancelled = true;
            if(widgetIdRef.current && window.turnstile){
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [siteKey, action]);

    return <div ref={containerRef} className="flex justify-center" />;
});

Turnstile.propTypes = {
    action: PropTypes.string.isRequired,
    onVerify: PropTypes.func.isRequired,
    onUnavailable: PropTypes.func,
};

export default Turnstile;
